import React, { useState, useEffect } from 'react';
import { Shield, Plus, CheckCircle, Clock, Users, XCircle, Trash2 } from 'lucide-react';
import { useModal } from '../ui/ModalProvider';

export default function EmployeeManager() {
  const { showConfirm } = useModal();
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const resPending = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resPending.ok) setPending(await resPending.json());

      const resActive = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resActive.ok) setActive(await resActive.json());
    } catch (e) {
      console.error(e);
    }
  };

  const handleGenerateLink = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/generate-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: newEmail, role: 'employee' })
      });
      if (res.ok) {
        const data = await res.json();
        setGeneratedLink(window.location.origin + data.link);
        setNewEmail('');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleAction = (id, action) => {
    const executeAction = async () => {
      try {
        const token = localStorage.getItem('token');
        let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/employees/${id}`;
        let method = 'POST';
        
        if (action === 'verify') url += '/verify';
        else if (action === 'suspend') url += '/suspend';
        else if (action === 'delete') method = 'DELETE';

        const res = await fetch(url, {
          method,
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          fetchEmployees();
        }
      } catch (e) {
        console.error(e);
      }
    };

    if (action === 'delete') {
      showConfirm("Are you sure you want to permanently delete this employee and ALL their tasks/timelogs?", executeAction);
    } else {
      executeAction();
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-black/40 backdrop-blur-md p-6 rounded-xl border border-white/10">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-silver" />
          Generate Registration Link
        </h2>
        <form onSubmit={handleGenerateLink} className="flex gap-4">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Employee Email Address"
            className="flex-1 bg-black/50 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-silver"
            required
          />
          <button type="submit" className="bg-white text-black px-6 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors">
            Generate Link
          </button>
        </form>
        {generatedLink && (
          <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 text-green-400 rounded-lg text-sm break-all">
            Link Generated (Expires in 7 days): <br />
            <strong>{generatedLink}</strong>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-black/40 backdrop-blur-md p-6 rounded-xl border border-white/10">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-silver" />
            Pending Approvals
          </h2>
          {pending.length === 0 ? (
            <p className="text-gray-400">No employees pending approval.</p>
          ) : (
            <div className="space-y-3">
              {pending.map(employee => (
                <div key={employee.id} className="flex flex-col gap-3 p-4 bg-black/50 border border-white/10 rounded-lg">
                  <div 
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setSelectedEmployee(employee)}
                  >
                    <div className="text-white font-medium">{employee.email} {employee.name && `(${employee.name})`}</div>
                    <div className="text-sm text-gray-400">Registered: {new Date(employee.createdAt).toLocaleDateString()}</div>
                    <div className="text-xs text-silver mt-1">Click to view full details &rarr;</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAction(employee.id, 'verify')}
                      className="flex-1 flex items-center justify-center gap-2 bg-silver/20 text-silver hover:bg-silver hover:text-black px-4 py-2 rounded-lg transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => handleAction(employee.id, 'delete')}
                      className="flex-1 flex items-center justify-center gap-2 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-black/40 backdrop-blur-md p-6 rounded-xl border border-white/10">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-silver" />
            Active Employees
          </h2>
          {active.length === 0 ? (
            <p className="text-gray-400">No active employees.</p>
          ) : (
            <div className="space-y-3">
              {active.map(employee => (
                <div key={employee.id} className="flex items-center justify-between p-4 bg-black/50 border border-white/10 rounded-lg">
                  <div 
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setSelectedEmployee(employee)}
                  >
                    <div className="text-white font-medium">{employee.email} {employee.name && `(${employee.name})`}</div>
                    <div className="text-[11px] uppercase font-bold text-green-400 mt-1 px-1.5 py-0.5 rounded border border-green-400/20 bg-green-400/10 w-fit">Active</div>
                    <div className="text-xs text-gray-500 mt-1">Click to view details</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAction(employee.id, 'suspend')}
                      className="p-2 text-yellow-500 hover:bg-yellow-500 hover:text-black rounded-lg transition-colors bg-yellow-500/10"
                      title="Suspend Access"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleAction(employee.id, 'delete')}
                      className="p-2 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-colors bg-red-500/10"
                      title="Delete Employee"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#111] border border-white/10 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">Employee Details</h3>
              <button onClick={() => setSelectedEmployee(null)} className="text-gray-400 hover:text-white transition-colors">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><span className="text-gray-500 block mb-1">Name</span><span className="text-white font-medium">{selectedEmployee.name || 'N/A'}</span></div>
                <div><span className="text-gray-500 block mb-1">Email</span><span className="text-white">{selectedEmployee.email}</span></div>
                <div><span className="text-gray-500 block mb-1">Phone</span><span className="text-white">{selectedEmployee.phone || 'N/A'}</span></div>
                <div><span className="text-gray-500 block mb-1">Qualification</span><span className="text-white">{selectedEmployee.qualification || 'N/A'}</span></div>
                <div><span className="text-gray-500 block mb-1">PAN Number</span><span className="text-white uppercase">{selectedEmployee.panNumber || 'N/A'}</span></div>
                <div><span className="text-gray-500 block mb-1">Aadhar Number</span><span className="text-white">{selectedEmployee.aadharNumber || 'N/A'}</span></div>
              </div>
              <div className="pt-4 border-t border-white/10">
                <span className="text-gray-500 block mb-2">Reason to Join</span>
                <p className="text-white whitespace-pre-wrap bg-black/30 p-3 rounded-lg border border-white/5">{selectedEmployee.reasonToJoin || 'N/A'}</p>
              </div>
              <div className="pt-4 border-t border-white/10">
                <span className="text-gray-500 block mb-2">Goals & Objectives</span>
                <p className="text-white whitespace-pre-wrap bg-black/30 p-3 rounded-lg border border-white/5">{selectedEmployee.goals || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
